import React, { useEffect, useState } from 'react';
import { Boxes, Bot, Wrench, Server, Download } from 'lucide-react';
import { useApi } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1B7A3D', amber: '#8A6D1D', teal: '#0E7C7B', line: '#E4E9EF' };
const STATUS = (s) => s?.startsWith('live') ? { bg: '#E8F4EC', fg: C.green }
  : s === 'partial' ? { bg: '#FDF6E3', fg: C.amber } : { bg: '#EEF1F5', fg: C.slate };

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-lg border px-4 py-3" style={{ borderColor: C.line, background: '#fff' }} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider" style={{ color: C.slate }}><Icon size={12} /> {label}</div>
      <div className="text-2xl font-bold font-mono mt-1" style={{ color: C.navy }}>{value}</div>
    </div>
  );
}

function Chip({ status }) {
  const s = STATUS(status);
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase whitespace-nowrap" style={{ background: s.bg, color: s.fg }}>{status}</span>;
}

export default function AgentRegistryPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [tools, setTools] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/agents').then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || 'Could not load the registry'));
    api.get('/agents/tools').then(r => setTools(r.data)).catch(() => {});
  }, [api]);

  if (err) return <div className="p-6 text-sm" style={{ color: C.slate }} data-testid="agent-registry-error">{err}</div>;
  if (!data) return <div className="p-6 text-xs" style={{ color: C.slate }}>Loading registry…</div>;

  const c = data.counts;
  const types = Object.entries(data.agent_types);
  const det = tools ? Object.entries(tools.tools).filter(([, t]) => t.kind === 'deterministic') : [];
  const models = tools ? Object.entries(tools.tools).filter(([, t]) => t.kind === 'model') : [];
  const families = [...new Set(det.map(([, t]) => t.family))];

  return (
    <div className="space-y-5" data-testid="agent-registry-page">
      <div className="rounded-lg border px-4 py-3 text-xs" style={{ borderColor: C.line, background: '#fff', color: C.navy }} data-testid="agent-registry-description">
        <Boxes size={13} className="inline mr-1" style={{ color: C.teal }} />
        <b>{data.registry_version}</b> — the registry is the only counter. {data.external_description}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat icon={Bot} label="Agent types" value={c.agent_types} testid="stat-agent-types" />
        <Stat icon={Bot} label="Live / partial" value={`${c.agent_types_live} / ${c.agent_types_partial.length}`} testid="stat-agent-live" />
        <Stat icon={Wrench} label="Deterministic tools" value={c.tools_deterministic} testid="stat-tools" />
        <Stat icon={Wrench} label="Tools real" value={c.tools_deterministic_by_status.real || 0} testid="stat-tools-real" />
        <Stat icon={Server} label="Platform services" value={c.services} testid="stat-services" />
      </div>

      <div className="rounded-lg border overflow-hidden" style={{ borderColor: C.line, background: '#fff' }}>
        <table className="w-full text-left">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider border-b" style={{ color: C.slate, borderColor: C.line }}>
              <th className="px-4 py-2.5">Agent type</th><th className="px-3 py-2.5">Seam</th><th className="px-3 py-2.5">Ceiling</th>
              <th className="px-3 py-2.5">Level today</th><th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5">Model family</th>
              <th className="px-3 py-2.5">Tools</th><th className="px-3 py-2.5">Conditions</th>
            </tr>
          </thead>
          <tbody>
            {types.map(([id, a]) => (
              <tr key={id} className="border-b last:border-b-0 align-top" style={{ borderColor: C.line }} data-testid={`agent-type-row-${id}`}>
                <td className="px-4 py-2.5">
                  <div className="text-sm font-semibold" style={{ color: C.navy }}>{a.name}</div>
                  <div className="text-[10px]" style={{ color: C.slate }}>{a.owner}</div>
                </td>
                <td className="px-3 py-2.5 text-[11px]" style={{ color: C.slate }}>{a.seam}</td>
                <td className="px-3 py-2.5 text-[11px] font-mono" style={{ color: C.navy }}>{a.ceiling}</td>
                <td className="px-3 py-2.5 text-[11px] font-mono" style={{ color: C.navy }}>{a.level_today || '—'}</td>
                <td className="px-3 py-2.5"><Chip status={a.status} /></td>
                <td className="px-3 py-2.5 text-[11px]" style={{ color: C.slate }}>{a.model_family || '—'}</td>
                <td className="px-3 py-2.5 text-[11px] font-mono" style={{ color: C.navy }}>{a.tools}</td>
                <td className="px-3 py-2.5 text-[11px] max-w-xs" style={{ color: C.slate }}>{a.conditions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {Object.entries(data.services).map(([id, s]) => (
          <div key={id} className="rounded-lg border px-4 py-3" style={{ borderColor: C.line, background: '#fff' }} data-testid={`service-${id}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold" style={{ color: C.navy }}><Server size={13} className="inline mr-1" style={{ color: C.teal }} />{s.name}</div>
              <Chip status={s.status} />
            </div>
            <p className="text-[11px] mt-1.5" style={{ color: C.slate }}>{s.note}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {Object.entries(s.components).map(([k, v]) => (
                <span key={k} className="text-[10px] px-1.5 py-0.5 rounded font-mono" style={{ background: '#F5F7FA', color: C.navy }}>{k}: {v}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {tools && (
        <div className="rounded-lg border px-4 py-3" style={{ borderColor: C.line, background: '#fff' }} data-testid="tools-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold" style={{ color: C.navy }}><Wrench size={13} className="inline mr-1" style={{ color: C.teal }} />
              {det.length} deterministic tools · {models.length} model capabilities</div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/agents/roster-recut.csv`} className="text-[11px] inline-flex items-center gap-1" style={{ color: C.teal }} data-testid="roster-recut-csv-link">
              <Download size={11} /> A3 roster re-cut (CSV)
            </a>
          </div>
          {families.map(f => (
            <div key={f} className="mb-2">
              <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.slate }}>{f}</div>
              <div className="flex flex-wrap gap-1.5">
                {det.filter(([, t]) => t.family === f).map(([id, t]) => (
                  <span key={id} title={`${t.module || 'no module yet'} — ${t.note}`} className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                    style={{ background: t.status === 'real' ? '#E8F4EC' : '#EEF1F5', color: t.status === 'real' ? C.green : C.slate }} data-testid={`tool-${id}`}>
                    {id}{t.status !== 'real' ? ` · ${t.status}` : ''}
                  </span>
                ))}
              </div>
            </div>
          ))}
          <div className="text-[10px] uppercase tracking-wider mb-1 mt-3" style={{ color: C.slate }}>model capabilities (never decisions of record)</div>
          <div className="flex flex-wrap gap-1.5">
            {models.map(([id, t]) => (
              <span key={id} title={t.note} className="text-[10px] px-1.5 py-0.5 rounded font-mono" style={{ background: '#FDF6E3', color: C.amber }} data-testid={`model-${id}`}>
                {id} · {t.provider || t.status}
              </span>
            ))}
          </div>
        </div>
      )}

      <ul className="text-[11px] list-disc pl-5" style={{ color: C.slate }} data-testid="agent-guardrails">
        {data.guardrails.map(g => <li key={g}>{g}</li>)}
      </ul>
    </div>
  );
}
